import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as config from "../constants/config";
import * as modelHelper from "../helpers/modelHelper";
import * as reducerHelper from "../helpers/reducerHelper";
import * as changelogService from "../services/changelogService";

import Request from "../models/utils/Request";

const initialState = {
    activeServer: null,
    orderServer: null,
    connected: null,
    compatible: false,
    activeDatabase: null,
    availableDatabase: false,
    compatibleDatabase: false,
    updatingDatabase: false,
    numberOfUpdates: 0,
    sequence: config.SERVER_INIT_SEQUENCE,
    version: changelogService.getLastServerVersion(),
    requests: [],
    multicastServerList: [],
    serverManagementList: [],
    compatibleServerList: [],
    databaseList: [],
    updateDatabaseList: [],
    invalidDatabaseList: [],
    downloadingFileList: [],
    excelFileStats: null,
    excelFileStep: null,
    excelFileTotal: null,
    excelFileItem: null,
    excelFileResult: null,
    excelFileLog: null
};

/**
 * Structure of server store:
 * - activeServer {Object}                  - active server object
 * - orderServer {Object}                   - first object in array of compatible server list
 * - connected {Boolean}                    - if true, client is connected to server
 * - compatible {Boolean}                   - if false, client-server versions are not compatible
 * - availableDatabase {Boolean}            - if true, at least one database is available
 * - compatibleDatabase {Boolean}           - if true, at least one database is compatible with current server
 * - updatingDatabase {Boolean}             - if true, update of databse structure is in progress
 * - numberOfUpdates {Number}               - number of database update attempts in a row
 * - activeDatabase {Object}                - currently connected database
 * - version {String}                       - last server version from changelog
 * - sequence {Number}                      - autoincrement for client - server communication
 * - requests {Array}                       - array of server requests that are waiting for response
 * - multicastServerList {Array}            - list of received available servers from multicast
 * - serverManagementList {Array}           - list of all servers (compatibleOnly = false and activeOnly = false)
 * - compatibleServerList {Array}           - list of available servers (compatibleOnly = true, activeOnly = true, ordering = true)
 * - databaseList {Array}                   - list of all valid and compatible databases
 * - updateDatabaseList {Array}             - list of databases that need update
 * - invalidDatabaseList {Array}            - list of all invalid databases
 * - downloadingFileList {Array}            - list of downloading Files
 * - excelFileStats {Object | null}         - state of excel file analysis
 * - excelFileStats {boolean | null}        - results of excel file analysis
 * - excelFileStats {String | null}         - log of excel file analysis
 */
export const serverReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.SAVE_MULTICAST_SERVER_LIST:
            return {
                ...state,
                multicastServerList: modelHelper.mapServersToObject(action.payload.servers)
            };
        case actionTypes.SAVE_SERVER_MANAGEMENT_LIST:
            return {
                ...state,
                serverManagementList: modelHelper.mapAvailableServersToObject(action.payload.serverList)
            };
        case actionTypes.SET_ACTIVE_SERVER:
            return {
                ...state,
                activeServer: action.payload.server
            };
        case actionTypes.SERVER_CONNECT:
        case actionTypes.SERVER_RECONNECT:
            return {
                ...state,
                connected: null
            };
        case actionTypes.WEBSOCKET_OPENED:
            return {
                ...state,
                connected: true
            };
        case actionTypes.WEBSOCKET_CLOSED:
            return {
                ...state,
                orderServer: null,
                connected: false,
                activeDatabase: null,
                updatingDatabase: false,
                databaseList: [],
                updateDatabaseList: [],
                invalidDatabaseList: [],
                requests: [],
                multicastServerList: [],
                serverManagementList: [],
                compatibleServerList: []
            };
        case actionTypes.SERVER_COMPATIBLE:
            return {
                ...state,
                compatible: true
            };
        case actionTypes.SERVER_NOT_COMPATIBLE:
            return {
                ...state,
                compatible: false
            };
        case actionTypes.DATABASE_AVAILABLE:
            return {
                ...state,
                availableDatabase: true
            };
        case actionTypes.DATABASE_NOT_AVAILABLE:
            return {
                ...state,
                availableDatabase: false
            };
        case actionTypes.DATABASE_COMPATIBLE:
            return {
                ...state,
                compatibleDatabase: true,
                numberOfUpdates: 0
            };
        case actionTypes.SAVE_ACTIVE_DATABASE:
            return {
                ...state,
                activeDatabase: action.payload.database
            };
        case actionTypes.INTERGAMMA_SELECT_DATABASE:
        case actionTypes.INTERGAMMA_SET_ACTIVE_DATABASE:
        case actionTypes.INTERGAMMA_MACHINE_SET_ACTIVE_DATABASE:
        case actionTypes.INTERGAMMA_SET_ACTIVE_DATABASE_AFTER_BARCODE:
        case actionTypes.INTERGAMMA_SET_ACTIVE_DATABASE_AFTER_CIF_BARCODE:
            return {
                ...state,
                activeDatabase: action.payload.database
            };
        case actionTypes.SAVE_DATABASE_LIST:
            return {
                ...state,
                databaseList: reducerHelper.updateDatabaseList(action.payload.databaseList)
            };
        case actionTypes.DATABASE_NOT_COMPATIBLE:
            return {
                ...state,
                compatibleDatabase: false
            };
        case actionTypes.START_DATABASE_UPDATE:
        case actionTypes.DATABASE_UPDATE_IN_PROGRESS:
            return {
                ...state,
                updatingDatabase: true
            };
        case actionTypes.DATABASE_UPDATE_FINISHED:
            return {
                ...state,
                updatingDatabase: false
            };
        case actionTypes.INCREASE_NUMBER_OF_DATABASE_UPDATES:
            return {
                ...state,
                numberOfUpdates: state.numberOfUpdates + 1
            };
        case actionTypes.SAVE_WEBSOCKET_REQUEST:
            if (action.payload.method === undefined || action.payload.tabId === undefined) {
                return {
                    ...state,
                    sequence: action.payload.sequence
                };
            }

            let newRequests = [];
            let newRequest = new Request(
                action.payload.sequence,
                action.payload.tabId,
                action.payload.method,
                action.payload.next
            );

            for (let request of state.requests) {
                if (
                    newRequest.tabId !== componentTypes.NO_SKIP_TAB_ID &&
                    request.tabId === newRequest.tabId &&
                    request.method === newRequest.method
                ) {
                    newRequests.push(request.skipRequest());
                } else {
                    newRequests.push(request);
                }
            }

            newRequests.push(newRequest);

            return {
                ...state,
                sequence: action.payload.sequence,
                requests: newRequests
            };
        case actionTypes.SKIP_WEBSOCKET_REQUEST:
            let skippedRequests = [];

            let activeRequest;
            for (activeRequest of state.requests) {
                if (activeRequest.tabId === action.payload.tabId && activeRequest.method === action.payload.method) {
                    skippedRequests.push(activeRequest.skipRequest());
                } else {
                    skippedRequests.push(activeRequest);
                }
            }

            return {
                ...state,
                requests: skippedRequests
            };
        case actionTypes.REMOVE_WEBSOCKET_REQUEST:
            return {
                ...state,
                requests: state.requests.filter((request) => request.id !== action.payload.sequence)
            };
        case actionTypes.SAVE_ACTIVE_SERVER_URL:
            return {
                ...state,
                activeServer: modelHelper.getServerObjectFromUrl(action.payload.url)
            };
        case actionTypes.CLOSE_SERVER_CONNECTION:
            return {
                ...state,
                connected: null,
                compatible: false,
                availableDatabase: false,
                compatibleDatabase: false,
                updatingDatabase: false,
                sequence: action.payload.sequence
            };
        case actionTypes.SAVE_COMPATIBLE_SERVER_LIST:
            let compatibleServerList = modelHelper.mapAvailableServersToObject(action.payload.compatibleServerList);

            let newOrderServer = null;
            if (compatibleServerList.length > 0) {
                newOrderServer = compatibleServerList.find((server) => server.enabled === true);

                if (newOrderServer === undefined) {
                    newOrderServer = null;
                }
            }

            return {
                ...state,
                orderServer: newOrderServer,
                compatibleServerList: compatibleServerList
            };
        case actionTypes.SAVE_PROCESSED_COMPATIBLE_SERVER_LIST:
            let newCompatibleOrderServer = null;
            if (action.payload.compatibleServerList.length > 0) {
                newCompatibleOrderServer = action.payload.compatibleServerList.find(
                    (server) => server.enabled === true
                );

                if (newCompatibleOrderServer === undefined) {
                    newCompatibleOrderServer = null;
                }
            }

            return {
                ...state,
                orderServer: newCompatibleOrderServer,
                compatibleServerList: action.payload.compatibleServerList
            };
        case actionTypes.SAVE_SERVER_INFO:
            return {
                ...state,
                activeServer: state.activeServer.updateServerData(
                    action.payload.data.uuid,
                    action.payload.data.version,
                    action.payload.data.name
                )
            };
        case actionTypes.SAVE_SERVER_CONFIGURATION:
            return {
                ...state,
                activeServer: state.activeServer.updateMulticast(action.payload.multicast)
            };
        case actionTypes.SAVE_COMPUTER_ID:
            return {
                ...state,
                activeServer: state.activeServer.updateComputerId(action.payload.data.computerId)
            };
        case actionTypes.SAVE_ALL_DATABASE_LIST:
            let allDatabaseList = modelHelper.mapAllDatabaseList(action.payload.databaseList);

            return {
                ...state,
                databaseList: modelHelper.getValidDatabaseList(allDatabaseList),
                updateDatabaseList: modelHelper.getUpdateDatabaseList(allDatabaseList),
                invalidDatabaseList: modelHelper.getInvalidDatabaseList(allDatabaseList)
            };
        case actionTypes.SERVER_SAVE_FILE_INFORMATION:
            return {
                ...state,
                downloadingFileList: state.downloadingFileList.concat(action.payload.downloadingFile)
            };
        case actionTypes.SERVER_UPDATE_FILE:
            return {
                ...state,
                downloadingFileList: state.downloadingFileList.map((item) =>
                    item.id === action.payload.preparedFileId ? action.payload.downloadingFile : item
                )
            };
        case actionTypes.SERVER_UPDATE_FILE_INFORMATION:
            return {
                ...state,
                downloadingFileList: state.downloadingFileList.map((item) =>
                    item.id === action.payload.downloadingFile.id ? action.payload.downloadingFile : item
                )
            };
        case actionTypes.SERVER_DELETE_DOWNLOADED_FILE:
            return {
                ...state,
                downloadingFileList: state.downloadingFileList.filter(
                    (item) => item.id !== action.payload.downloadedFileId
                )
            };
        case actionTypes.SAVE_EXCEL_FILE_ANALYSIS:
            return {
                ...state,
                excelFileStats: action.payload.stats
            };
        case actionTypes.SAVE_EXCEL_FILE_ITEM:
            return {
                ...state,
                excelFileItem: action.payload.item,
                excelFileTotal: action.payload.total
            };
        case actionTypes.SAVE_EXCEL_FILE_STEP:
            return {
                ...state,
                excelFileStep: action.payload.step
            };
        case actionTypes.SAVE_EXCEL_FILE_RESULT:
            return {
                ...state,
                excelFileResult: action.payload.result,
                excelFileLog: action.payload.log
            };
        case actionTypes.NAVIGATION_CLOSE_MODAL:
            if (action.payload.type === componentTypes.DATABASE_FROM_EXCEL_MODAL) {
                return {
                    ...state,
                    excelFileStats: null,
                    excelFileResult: null,
                    excelFileLog: null,
                    excelFileItem: null,
                    excelFileStep: null,
                    excelFileTotal: null
                };
            }
            return state;
        default:
            return state;
    }
};
